"use client";

import { useState, useCallback } from "react";
import QRCode from "qrcode";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Stack,
  Fade,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Collapse,
} from "@mui/material";
import {
  QrCode2,
  Delete,
  Download,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  Image as ImageIcon,
} from "@mui/icons-material";

type ImageFormat = "PNG" | "JPEG" | "WebP" | "SVG";
type ErrorLevel = "L" | "M" | "Q" | "H";

const ERROR_LEVELS: { value: ErrorLevel; label: string; hint: string }[] = [
  { value: "L", label: "Low (~7%)", hint: "Smallest, less resilient" },
  { value: "M", label: "Medium (~15%)", hint: "Balanced (default)" },
  { value: "Q", label: "Quartile (~25%)", hint: "Good for prints" },
  { value: "H", label: "High (~30%)", hint: "Max resilience, damaged/dirty" },
];

function hexToRgbaHex(hex: string, alpha: string = "FF"): string {
  const h = hex.replace(/^#/, "");
  if (h.length === 6) return `#${h}${alpha}`;
  if (h.length === 8) return `#${h}`;
  return `#${h}00${alpha}`.slice(0, 9);
}

export default function TextToQrCodeConverter() {
  const [input, setInput] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [svgString, setSvgString] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Options
  const [errorLevel, setErrorLevel] = useState<ErrorLevel>("M");
  const [size, setSize] = useState(256);
  const [margin, setMargin] = useState(4);
  const [format, setFormat] = useState<ImageFormat>("PNG");
  const [quality, setQuality] = useState(0.92);
  const [fgColor, setFgColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#FFFFFF");
  const [transparentBg, setTransparentBg] = useState(false);
  const [version, setVersion] = useState<number | "">("");

  const buildOptions = useCallback(() => {
    const light =
      transparentBg && (format === "PNG" || format === "SVG")
        ? "#FFFFFF00"
        : hexToRgbaHex(bgColor);
    const dark = hexToRgbaHex(fgColor);

    const base: Parameters<typeof QRCode.toDataURL>[1] & {
      color?: { dark: string; light: string };
      width?: number;
      margin?: number;
      version?: number;
    } = {
      errorCorrectionLevel: errorLevel,
      margin,
      width: size,
      color: { dark, light },
      ...(version !== "" && version >= 1 && version <= 40 ? { version } : {}),
    };

    if (format === "JPEG" || format === "WebP") {
      return {
        ...base,
        type: format === "JPEG" ? "image/jpeg" : "image/webp",
        rendererOpts: { quality },
      };
    }
    if (format === "PNG") {
      return { ...base, type: "image/png" as const };
    }
    // SVG: use in toString
    return base;
  }, [
    errorLevel,
    margin,
    size,
    format,
    quality,
    fgColor,
    bgColor,
    transparentBg,
    version,
  ]);

  const generate = useCallback(async () => {
    setError(null);
    const text = input.trim();
    if (!text) {
      setError("Please enter text or a URL to encode.");
      return;
    }

    debugger;

    try {
      const opts = buildOptions();
      if (format === "SVG") {
        const svg = await QRCode.toString(text, {
          ...opts,
          type: "svg",
        });
        setSvgString(svg);
        setQrDataUrl(`data:image/svg+xml,${encodeURIComponent(svg)}`);
      } else {
        const dataUrl = QRCode.toDataURL(
          text,
          opts as never
        ) as unknown as string;
        setQrDataUrl(dataUrl);
        setSvgString(null);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to generate QR code."
      );
      setQrDataUrl(null);
      setSvgString(null);
    }
  }, [input, format, buildOptions]);

  const handleDownload = useCallback(() => {
    if (!qrDataUrl && !svgString) return;
    const ext = format.toLowerCase();

    if (format === "SVG" && svgString) {
      const blob = new Blob([svgString], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qrcode.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }
    if (qrDataUrl) {
      const a = document.createElement("a");
      a.href = qrDataUrl;
      a.download = `qrcode.${ext}`;
      a.click();
    }
  }, [qrDataUrl, svgString, format]);

  const handleCopy = useCallback(async () => {
    if (!qrDataUrl && !svgString) return;
    try {
      if (format === "SVG" && svgString) {
        await navigator.clipboard.writeText(svgString);
      } else if (qrDataUrl) {
        const res = await fetch(qrDataUrl);
        const blob = await res.blob();
        await navigator.clipboard.write([
          new ClipboardItem({ [blob.type]: blob }),
        ]);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Failed to copy to clipboard.");
    }
  }, [qrDataUrl, svgString, format]);

  const handleClear = () => {
    setInput("");
    setQrDataUrl(null);
    setSvgString(null);
    setError(null);
    setCopied(false);
  };

  const hasOutput = !!qrDataUrl || !!svgString;
  const canCopyAsImage = format !== "SVG" && !!qrDataUrl;

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Text or URL to encode
        </Typography>
        <TextField
          multiline
          rows={4}
          fullWidth
          placeholder="Enter text, URL, vCard, Wi‑Fi config, or any string to encode..."
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
            if (hasOutput) {
              setQrDataUrl(null);
              setSvgString(null);
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontFamily: "monospace",
              fontSize: "0.9rem",
            },
          }}
        />

        {/* Main options */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Error correction</InputLabel>
              <Select
                value={errorLevel}
                label="Error correction"
                onChange={(e) => setErrorLevel(e.target.value as ErrorLevel)}
              >
                {ERROR_LEVELS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="body2" gutterBottom>
              Size: {size}×{size} px
            </Typography>
            <Slider
              value={size}
              onChange={(_, v) => setSize(v as number)}
              min={128}
              max={512}
              step={32}
              valueLabelDisplay="auto"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Format</InputLabel>
              <Select
                value={format}
                label="Format"
                onChange={(e) => setFormat(e.target.value as ImageFormat)}
              >
                <MenuItem value="PNG">PNG</MenuItem>
                <MenuItem value="JPEG">JPEG</MenuItem>
                <MenuItem value="WebP">WebP</MenuItem>
                <MenuItem value="SVG">SVG</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Quality (JPEG/WebP) */}
        {(format === "JPEG" || format === "WebP") && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" gutterBottom>
              Quality: {Math.round(quality * 100)}%
            </Typography>
            <Slider
              value={quality}
              onChange={(_, v) => setQuality(v as number)}
              min={0.1}
              max={1}
              step={0.05}
              valueLabelDisplay="auto"
              size="small"
              sx={{ maxWidth: 280 }}
            />
          </Box>
        )}

        {/* Advanced toggle */}
        <Button
          size="small"
          startIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setShowAdvanced(!showAdvanced)}
          sx={{ mt: 2 }}
        >
          {showAdvanced ? "Hide" : "Show"} advanced options
        </Button>
        <Collapse in={showAdvanced}>
          <Grid container spacing={3} sx={{ mt: 1, mb: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" gutterBottom>
                Margin (quiet zone): {margin}
              </Typography>
              <Slider
                value={margin}
                onChange={(_, v) => setMargin(v as number)}
                min={0}
                max={8}
                step={1}
                valueLabelDisplay="auto"
                size="small"
                sx={{ maxWidth: 280 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                size="small"
                label="QR version (1–40)"
                placeholder="Auto"
                type="number"
                value={version}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "") setVersion("");
                  else {
                    const n = parseInt(v, 10);
                    if (!isNaN(n) && n >= 1 && n <= 40) setVersion(n);
                  }
                }}
                inputProps={{ min: 1, max: 40 }}
                sx={{ maxWidth: 200 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2">Foreground</Typography>
                <input
                  type="color"
                  value={fgColor}
                  onChange={(e) => setFgColor(e.target.value)}
                  style={{
                    width: 40,
                    height: 32,
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                />
                <TextField
                  size="small"
                  value={fgColor}
                  onChange={(e) => setFgColor(e.target.value)}
                  sx={{ width: 100 }}
                  inputProps={{ maxLength: 7 }}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                flexWrap="wrap"
              >
                <Typography variant="body2">Background</Typography>
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  disabled={transparentBg}
                  style={{
                    width: 40,
                    height: 32,
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    cursor: transparentBg ? "not-allowed" : "pointer",
                    opacity: transparentBg ? 0.6 : 1,
                  }}
                />
                <TextField
                  size="small"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  disabled={transparentBg}
                  sx={{ width: 100 }}
                  inputProps={{ maxLength: 7 }}
                />
                {(format === "PNG" || format === "SVG") && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={transparentBg}
                        onChange={(e) => setTransparentBg(e.target.checked)}
                      />
                    }
                    label="Transparent"
                  />
                )}
              </Stack>
            </Grid>
          </Grid>
        </Collapse>

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 3 }}
          flexWrap="wrap"
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<QrCode2 sx={{ color: "#ffffff" }} />}
            onClick={generate}
            disabled={!input.trim()}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: "1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            Generate QR Code
          </Button>
          <Button
            variant="outlined"
            startIcon={<Delete />}
            onClick={handleClear}
          >
            Clear
          </Button>
        </Stack>

        {hasOutput && (
          <Fade in={hasOutput}>
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  QR Code
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button
                    size="small"
                    startIcon={<Download />}
                    onClick={handleDownload}
                    variant="outlined"
                  >
                    Download {format}
                  </Button>
                  <Button
                    size="small"
                    startIcon={copied ? <CheckCircle /> : <ImageIcon />}
                    onClick={handleCopy}
                    variant="outlined"
                    color={copied ? "success" : "primary"}
                    disabled={format === "SVG" ? false : !canCopyAsImage}
                  >
                    {copied
                      ? "Copied!"
                      : format === "SVG"
                        ? "Copy SVG"
                        : "Copy image"}
                  </Button>
                </Stack>
              </Box>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: transparentBg ? "transparent" : "grey.100",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 200,
                }}
              >
                {qrDataUrl && (
                  <Box
                    component="img"
                    src={qrDataUrl}
                    alt="Generated QR code"
                    sx={{
                      maxWidth: "100%",
                      height: "auto",
                      display: "block",
                    }}
                  />
                )}
              </Paper>
            </Box>
          </Fade>
        )}
      </Paper>
    </Box>
  );
}
